// src/admin/legal/legalModel.js — the pure rules of «Configurazione» → «TOS & Privacy» (v219). No React, no I/O
// except `sniffPdf` (reads 5 bytes of a picked file).
//
// ⭐ The backend decides: the version in effect (`current`) and whether a row may change (`correctable`) come from
// the server and are NEVER computed here. «Stato» is display only: in force = `current[document]`; scheduled =
// `effective_from` later than the moment the list was READ (`readAt`, not the live clock — so the three states
// always agree with `current`, which is also as of the read); superseded = the rest.
// ⚠️ The server lists `privacy` before `tos` (ORDER BY document): `normaliseList` regroups TOS first.
// ⚠️ `url`, `pdf_url`, `sha256`, `size_bytes` may be null (versions recorded before links and copies existed).
// Limits mirror BE src/auth/legal_versions.py (version pattern, https ≤ 500 chars, PDF ≤ 5 MB starting %PDF-).

export const LEGAL_DOCUMENTS = Object.freeze(['tos', 'privacy']);
export const VERSION_PATTERN = /^[A-Za-z0-9._-]{1,40}$/;
export const URL_MAX = 500;
export const PDF_MAX_BYTES = 5 * 1024 * 1024;
const PDF_MAGIC = '%PDF-';

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const rank = (document) => {
  const i = LEGAL_DOCUMENTS.indexOf(document);
  return i === -1 ? LEGAL_DOCUMENTS.length : i;
};
const time = (iso) => {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
};

/** The GET body → `{ current: {tos, privacy}, versions, readAt }`. Never throws. */
export function normaliseList(raw, readAt) {
  const body = isObject(raw) ? raw : {};
  const rawCurrent = isObject(body.current) ? body.current : {};
  const current = Object.fromEntries(LEGAL_DOCUMENTS.map((d) => [
    d, typeof rawCurrent[d] === 'string' ? rawCurrent[d] : null,
  ]));
  const rows = (Array.isArray(body.versions) ? body.versions : [])
    .filter((r) => isObject(r) && typeof r.document === 'string' && typeof r.version === 'string');
  const versions = rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => (rank(a.r.document) - rank(b.r.document)) || (a.i - b.i))
    .map(({ r }) => r);
  return { current, versions, readAt };
}

/** 'inForce' | 'scheduled' | 'superseded' — see the header. */
export function statusOf(row, list) {
  if (list.current[row.document] === row.version) return 'inForce';
  const from = time(row.effective_from);
  if (from !== null && from > list.readAt) return 'scheduled';
  return 'superseded';
}

/** One entry per known document (TOS first): the version in effect, its row, and the EARLIEST scheduled row. */
export function summaryOf(list) {
  return LEGAL_DOCUMENTS.map((document) => {
    const version = list.current[document] ?? null;
    const own = list.versions.filter((r) => r.document === document);
    const row = version === null ? null : (own.find((r) => r.version === version) || null);
    const next = own
      .filter((r) => statusOf(r, list) === 'scheduled')
      .reduce((best, r) => (best === null || time(r.effective_from) < time(best.effective_from) ? r : best), null);
    return { document, version, row, next };
  });
}

/** The i18n key (admin namespace) of a known document's name; null for an unknown one (print its raw code). */
export function documentLabelKey(document) {
  return LEGAL_DOCUMENTS.includes(document) ? `legalDocuments.documents.${document}` : null;
}

/** «Correggi» is offered only when the server says so. Missing / null = no button (the 409 stays the judge). */
export function canCorrect(row) {
  return row?.correctable === true;
}

/** An absolute https:// address with a host, at most URL_MAX characters, no whitespace (BE check_url). */
export function isHttpsUrl(value) {
  if (typeof value !== 'string' || value.length > URL_MAX || /\s/.test(value)) return false;
  if (!value.startsWith('https://')) return false;
  try {
    return new URL(value).hostname !== '';
  } catch {
    return false;
  }
}

/** Sync check of a picked file: null when acceptable, else 'required' | 'pdfType' | 'pdfSize'. */
export function pdfProblem(file) {
  if (!file) return 'required';
  const name = typeof file.name === 'string' ? file.name.toLowerCase() : '';
  if (!name.endsWith('.pdf') || !(file.size > 0)) return 'pdfType';
  if (file.size > PDF_MAX_BYTES) return 'pdfSize';
  return null;
}

/** Async: does the file start with `%PDF-` (what the BE checks)? FileReader, not Blob.arrayBuffer (jsdom 16). */
export function sniffPdf(file) {
  return new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = () => {
        const bytes = new Uint8Array(reader.result || new ArrayBuffer(0));
        resolve(String.fromCharCode(...bytes) === PDF_MAGIC);
      };
      reader.onerror = () => resolve(false);
      reader.readAsArrayBuffer(file.slice(0, PDF_MAGIC.length));
    } catch {
      resolve(false);
    }
  });
}

const pad = (n) => String(n).padStart(2, '0');

/** A Date → the `<input type="datetime-local">` value, in LOCAL time ('YYYY-MM-DDTHH:mm'). */
export function toLocalInputValue(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** A datetime-local value (local time) → an ISO string with offset (UTC `Z`); null when empty or invalid. */
export function localToIso(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const date = new Date(value); // no offset in the string → local time (ECMAScript)
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

const linkProblem = (value) => (isHttpsUrl(value) ? null : 'https');

/** «Subito» → null; «Da una data» → 'required' (empty/invalid) | 'past' (at or before `now`) | null. */
function effectiveFromProblem(form, now) {
  if (form.when !== 'date') return null;
  const iso = localToIso(form.effectiveLocal);
  if (iso === null) return 'required';
  return Date.parse(iso) <= now ? 'past' : null;
}

/** The add form → `{ field: problem }` ({} = valid). The PDF's bytes are checked afterwards by `sniffPdf`. */
export function validateNewVersion(form, now) {
  const version = (form.version || '').trim();
  const problems = {
    document: LEGAL_DOCUMENTS.includes(form.document) ? null : 'required',
    version: version === '' ? 'required' : (VERSION_PATTERN.test(version) ? null : 'pattern'),
    url: linkProblem((form.url || '').trim()),
    pdfUrl: linkProblem((form.pdfUrl || '').trim()),
    file: pdfProblem(form.file),
    effectiveFrom: effectiveFromProblem(form, now),
  };
  return Object.fromEntries(Object.entries(problems).filter(([, p]) => p !== null));
}

/** The correction form vs the loaded row → only what differs: `{ url?, pdfUrl?, file? }` (trimmed; null ≡ ''). */
export function correctionChanges(form, row) {
  const url = (form.url || '').trim();
  const pdfUrl = (form.pdfUrl || '').trim();
  return {
    ...(url !== (row.url || '') ? { url } : {}),
    ...(pdfUrl !== (row.pdf_url || '') ? { pdfUrl } : {}),
    ...(form.file ? { file: form.file } : {}),
  };
}

/** Checks only the fields being sent. An emptied link is a change and fails the https check (never "clear it"). */
export function validateCorrection(changes) {
  const problems = {
    url: 'url' in changes ? linkProblem(changes.url) : null,
    pdfUrl: 'pdfUrl' in changes ? linkProblem(changes.pdfUrl) : null,
    file: 'file' in changes ? pdfProblem(changes.file) : null,
  };
  return Object.fromEntries(Object.entries(problems).filter(([, p]) => p !== null));
}

/** `3f5a9c01…e7c9` — first 8 + last 4. Short or missing values pass through. */
export function shortHash(sha) {
  if (typeof sha !== 'string') return null;
  return sha.length > 12 ? `${sha.slice(0, 8)}…${sha.slice(-4)}` : sha;
}

/** A date-time in the viewer's local time; '—' when missing or invalid. */
export function formatDateTime(iso, locale) {
  const ms = time(iso);
  if (ms === null) return '—';
  return new Date(ms).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
}

/** Bytes → '1,2 MB' / '340 KB' in the given locale; null when unknown. */
export function formatSize(bytes, locale) {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return null;
  const fmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  if (bytes >= 1024 * 1024) return `${fmt.format(bytes / (1024 * 1024))} MB`;
  return `${fmt.format(Math.max(1, Math.round(bytes / 1024)))} KB`;
}
